import * as React from "react";
import { observer } from "mobx-react-lite";
import { GVCMobx } from "../../mst/kinds/gvc";
import { NGLabelText } from "../../newcomponents/text/labelText";
import { NGSwitch } from "../../newcomponents/switch";
import { NGKindSelect } from "../../newcomponents/select/ngkindselect";
import { FormButtons } from "../../components/forms/formButtons";
import { ngParseLink } from "../../utils/linkParser/linkParser";
import { trySaveWithReleaseWarning } from "../../utils/releaseWarning";
import { GvcKeda as IGvcKeda } from "../../schema/types/gvc";
import { notification } from "antd";
import { useCleanPrompt } from "../../reactHooks/useCleanPrompt";
import { PromptContext } from "../../mobxStores/prompt/prompt";
import { arraysAreEqual, linksOf, request, resourceLink } from "../../services/cpln";
import { ConsoleContext } from "../../mobxStores/consoleContext/consoleContext";
import { Link } from "react-router-dom";
import { useTableKindId } from "../../newcomponents/table/data/useTableKindId";
import { useTableItemQueryData } from "../../newcomponents/table/data/useTableItemQueryData";
import { Secret } from "../../schema/types/secret";
import { AxiosResponse } from "axios";
import { sortBy } from "lodash";
import { TableModal } from "../../components/antd/TableModal";
import { NGButton } from "../../newcomponents/button/Button";
import { Table } from "../../newcomponents/table/table";
import { NameDescriptionNoLinkColumn } from "../../newcomponents/table/columns/wellKnown/nameDescriptionNoLinkColumn";
import { TagsColumn } from "../../newcomponents/table/columns/wellKnown/tagsColumn";
import { CreatedColumn } from "../../newcomponents/table/columns/wellKnown/createdColumn";
import { LastModifiedColumn } from "../../newcomponents/table/columns/wellKnown/lastModifiedColumn";
import { SecretTypeColumn } from "../../newcomponents/table/columns/secret/typeColumn";
import { PlusCircle } from "react-feather";
import { CustomTableCPLNQuery } from "../../newcomponents/table/components/TableCplnQuery";
import { TableHeaderRefreshButton } from "../../newcomponents/table/components/RefreshButton";

interface SecretTableItem extends Secret {
  selfLink: string;
}

interface Props {
  gvc: GVCMobx;
  patch: (body: any) => Promise<any>;
}
const GvcKedaRaw: React.FC<Props> = ({ gvc, patch }) => {
  const { kind, id } = useTableKindId("secret", "gvc-keda-secret-add");
  const qData = useTableItemQueryData<Secret>(kind, { fetchAllPages: true });

  function getDefaultLinks(): string[] {
    return [...(gvc.spec.keda?.secrets || [])];
  }
  function getDefaultCurrentItems(): SecretTableItem[] {
    return getDefaultLinks().map((link) => ({
      id: "",
      name: "",
      kind: "secret",
      version: 0,
      description: "",
      type: "" as any,
      tags: {},
      created: "",
      lastModified: "",
      links: [{ rel: "self", href: link }],
      email: ngParseLink(link, { useInputCtx: true }).name,
      selfLink: link,
    }));
  }
  const [currentItems, setCurrentItems] = React.useState<SecretTableItem[]>(getDefaultCurrentItems());
  function getCurrentLinks(): string[] {
    return currentItems.map((i) => i.selfLink);
  }

  React.useEffect(() => {
    fetchSecrets();
  }, []);

  async function fetchSecrets() {
    try {
      setIsLoading(true);
      const promises: Promise<AxiosResponse<Secret>>[] = [];
      for (const link of getDefaultLinks()) {
        promises.push(request({ url: link }));
      }
      const responses = await Promise.allSettled(promises);
      setCurrentItems((currentItems) => {
        const newItems: SecretTableItem[] = [];

        for (const responseIndexStr in responses) {
          const responseIndex = Number(responseIndexStr);
          const response = responses[responseIndex];

          if (response.status === "rejected") {
            let currentItem = currentItems[responseIndex];
            if (!currentItem) {
              currentItem = getDefaultCurrentItems()[responseIndex];
            }
            newItems.push(currentItem);
            continue;
          }

          const newItem = { ...response.value.data, selfLink: linksOf(response.value.data).self! };
          newItems.push(newItem);
        }

        return newItems;
      });
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
    }
  }

  const [selectionsToRemove, setSelectionsToRemove] = React.useState<string[]>([]);
  const [selectionsToAdd, setSelectionsToAdd] = React.useState<string[]>([]);
  const [selectionItemsToAdd, setSelectionItemsToAdd] = React.useState<SecretTableItem[]>([]);
  const [isAddingItem, setIsAddingItem] = React.useState(false);

  function onRemove() {
    setCurrentItems((items) => items.filter((item) => !selectionsToRemove.includes(item.selfLink)));
    setSelectionsToRemove([]);
  }

  function onOkAddModal() {
    setCurrentItems((items) => {
      const newItems: SecretTableItem[] = [...items];
      const currentLinks = items.map((i) => i.selfLink);

      for (const itemToAdd of selectionItemsToAdd) {
        if (currentLinks.includes(itemToAdd.selfLink)) {
          continue;
        }

        newItems.push(itemToAdd);
      }

      return sortBy(newItems, "email");
    });

    setSelectionsToAdd([]);
    setSelectionItemsToAdd([]);
  }

  const secretTableItems: SecretTableItem[] = qData.visibleItems
    .map((item) => ({
      ...item,
      selfLink: linksOf(item).self!,
    }))
    .filter((item) => !getCurrentLinks().includes(item.selfLink));

  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  function getDefaultEnabled() {
    return gvc.spec.keda?.enabled || false;
  }
  const [enabled, setEnabled] = React.useState<boolean>(getDefaultEnabled());
  function getDefaultIdentityName() {
    const res = ngParseLink(gvc.spec.keda?.identityLink || "", { useInputCtx: true });
    if (!res.isValid) {
      return "";
    }
    return res.name;
  }
  const [identityName, setIdentityName] = React.useState<string>(getDefaultIdentityName());

  const identityLink = React.useMemo(() => {
    if (!identityName) {
      return "";
    }
    return resourceLink("identity", identityName, { org: ConsoleContext.org, gvc: gvc.name });
  }, [identityName]);

  const isDirty = React.useMemo(() => {
    let res = false;
    if (enabled !== getDefaultEnabled()) res = true;
    if (identityName !== getDefaultIdentityName()) res = true;
    if (!arraysAreEqual(getCurrentLinks(), getDefaultLinks())) {
      res = true;
    }
    return res;
  }, [gvc.version, enabled, identityName, currentItems.length, gvc.spec.keda?.secrets.length]);

  React.useEffect(() => {
    PromptContext.setWhen(isDirty || isLoading);
  }, [isDirty, isLoading]);

  useCleanPrompt();

  function handleReset() {
    setEnabled(getDefaultEnabled());
    setIdentityName(getDefaultIdentityName());
    fetchSecrets();
    setSelectionsToRemove([]);
    setSelectionsToAdd([]);
    setSelectionItemsToAdd([]);
  }

  function trySave() {
    trySaveWithReleaseWarning(gvc, handleSave);
  }

  async function handleSave() {
    setIsLoading(true);

    try {
      const kedaConfig: IGvcKeda = { enabled: enabled };
      if (identityName) {
        kedaConfig.identityLink = identityLink;
      }
      if (getCurrentLinks().length > 0) {
        kedaConfig.secrets = getCurrentLinks();
      }

      await patch({ spec: { "$replace/keda": kedaConfig } });

      notification.success({
        message: "Success",
        description: "Updated KEDA",
      });
    } catch (e) {
      const errorMessage = e?.response?.data?.message || e.message;

      notification.warning({
        message: "Failed",
        description: errorMessage,
      });
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div key={gvc.version}>
      <div>
        <NGLabelText>KEDA</NGLabelText>
        <div className="text-sm mb-4">
          KEDA is a Kubernetes-based event-driven autoscaler that allows you to scale workloads based on external
          events.
        </div>
        <div className="mb-4">
          <NGLabelText>Logs</NGLabelText>
          <div className="text-sm">
            KEDA mounts a container named <span className="font-semibold">keda-operator</span> in gvc namespace to
            manage KEDA Autoscaling. This container can be queried for viewing logs.
          </div>
          <Link
            className="color-link"
            to={`/console/org/${ConsoleContext.org!}/logs?query=${encodeURIComponent(
              `{gvc="${gvc.name}", container="keda-operator"}`,
            )}&execute=true&live=false`}
          >
            View Logs
          </Link>
        </div>
        <NGSwitch value={enabled} onChange={(value) => setEnabled(value)}>
          Enable Keda
        </NGSwitch>
        <div className="text-sm mb-4">
          Enable KEDA for this GVC. When enabled, a keda operator will be deployed in the GVC and workloads in the GVC
          can use KEDA to scale based on external metrics.
        </div>
        <div className="mb-6">
          <NGLabelText>Identity Link</NGLabelText>
          <div className="text-sm mb-1">
            A link to an Identity resource that will be used for KEDA. This will allow the keda operator to access cloud
            and network resources.
          </div>
          <NGKindSelect
            kind={`identity`}
            value={identityName}
            onChange={(value) => setIdentityName(value)}
            queries={[{ rel: "gvc", value: gvc.name }]}
          />
        </div>
        <Table<SecretTableItem>
          tableId={"gvc-keda-secrets"}
          title={() => <NGLabelText>Secrets</NGLabelText>}
          data={currentItems}
          selectMode={"multi"}
          selectKey="selfLink"
          selections={selectionsToRemove}
          onSelectionsChange={setSelectionsToRemove}
          columns={[
            NameDescriptionNoLinkColumn(), //
            SecretTypeColumn(),
            TagsColumn(),
            CreatedColumn(),
            LastModifiedColumn(),
          ]}
          headerRenderer={() => (
            <>
              <NGButton
                variant={"danger"}
                size={"small"}
                outlined
                disabled={selectionsToRemove.length < 1}
                onClick={onRemove}
              >
                Remove
              </NGButton>
              <NGButton variant={"action"} size={"small"} onClick={() => setIsAddingItem(true)}>
                Add
              </NGButton>
            </>
          )}
          noItemsFoundRenderer={(table) => (
            <NGButton
              variant="primary"
              onClick={() => setIsAddingItem(true)}
              renderIcon={(_, props) => <PlusCircle {...props} />}
            >
              Add Secrets
            </NGButton>
          )}
        />
        <TableModal
          open={isAddingItem}
          onCancel={() => setIsAddingItem(false)}
          onOk={() => {
            onOkAddModal();
            setIsAddingItem(false);
          }}
        >
          <Table<SecretTableItem>
            tableId={id}
            title={"Select Secrets to Add"}
            selectMode="multi"
            selectKey="selfLink"
            selections={selectionsToAdd}
            onSelectionsChange={(keys, items) => {
              setSelectionsToAdd(keys);
              setSelectionItemsToAdd(items!);
            }}
            data={secretTableItems}
            headerRenderer={() => {
              return (
                <>
                  <TableHeaderRefreshButton onClick={qData.fetchItems} />
                  <CustomTableCPLNQuery
                    tableId={id}
                    kind={kind}
                    onQuery={qData.setQuery}
                    isLoading={qData.isLoadingQueried}
                  />
                </>
              );
            }}
            enableFilter
            isLoading={qData.isLoading}
            columns={[
              NameDescriptionNoLinkColumn(), //
              SecretTypeColumn(),
              TagsColumn(),
              CreatedColumn(),
              LastModifiedColumn(),
            ]}
          />
        </TableModal>
      </div>
      <div className="flex items-center">
        <FormButtons
          onReset={handleReset}
          onSave={trySave}
          saveDisabled={!isDirty || isLoading}
          resetDisabled={!isDirty || isLoading}
          loading={isLoading}
          disableOutlineOnReset
        />
      </div>
    </div>
  );
};

export const GvcKeda = observer(GvcKedaRaw);
